import { useEffect, useRef, useState } from 'react';
import {
    postPageLoadTelemetry,
    snapshotAndClearRequestIds,
} from '../services/telemetry';

export interface UsePageLoadTelemetryArgs {
    /** Page bucket — drives ELK aggregation. ``gutschrift-check-feed``
     *  keeps the #1289 farmer-facing feed separable from the canvas. */
    page: 'morgenlage' | 'documents' | 'farm' | 'gutschrift-check' | 'gutschrift-check-feed' | 'liquiditaetsplanung' | 'deckungsbeitrag' | 'ernteprognose';
    /** View descriptor (e.g. ``"farm:lense=organizations:sub=ohne-typ"``). */
    view: string;
    /** True while the page's primary data is being fetched. */
    isLoading: boolean;
    pageIndex?: number;
    pageSize?: number;
    /** True if React Query served the data from cache initially. */
    cacheHit?: boolean;
    /** ms before the overlay is allowed to render (default 1000). */
    showOverlayDelayMs?: number;
    /** ms above which the load is logged to ELK (default 1000, strict >). */
    telemetryThresholdMs?: number;
}

export interface UsePageLoadTelemetryReturn {
    /** Pass to ``<PageLoadOverlay visible={...} />``. */
    showOverlay: boolean;
    /** Pass to ``<PageLoadOverlay elapsedSeconds={...} />``. */
    elapsedSeconds: number;
}

const DEFAULT_OVERLAY_DELAY_MS = 1000;
const DEFAULT_TELEMETRY_THRESHOLD_MS = 1000;

/**
 * Track a page's primary load, gate the overlay on a flicker threshold,
 * and fire one telemetry POST per slow (>1 s) load.
 *
 * Designed for /farm and /documents — both have a single derivable
 * "primary load" boolean (FarmPage: useFarmBootstrap.isLoading ||
 * entitiesQuery.isLoading; DocumentsPage: useDocuments.loading).
 *
 * Per-tile sub-loads (e.g. evidence document fetch inside an expanded
 * entity) MUST NOT contribute to ``isLoading`` — pass only the
 * page-root signal. See issue #643 non-goals.
 */
export function usePageLoadTelemetry({
    page,
    view,
    isLoading,
    pageIndex,
    pageSize,
    cacheHit,
    showOverlayDelayMs = DEFAULT_OVERLAY_DELAY_MS,
    telemetryThresholdMs = DEFAULT_TELEMETRY_THRESHOLD_MS,
}: UsePageLoadTelemetryArgs): UsePageLoadTelemetryReturn {
    const [showOverlay, setShowOverlay] = useState(false);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);

    const startTimeRef = useRef<number | null>(null);
    const overlayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const tickIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const firedRef = useRef(false);

    // Capture the latest values into a ref so the load-end branch reads
    // the most-recent props without forcing the effect to re-fire on
    // every prop change (which would cancel the overlay timer).
    const propsRef = useRef({ page, view, pageIndex, pageSize, cacheHit, telemetryThresholdMs });
    propsRef.current = { page, view, pageIndex, pageSize, cacheHit, telemetryThresholdMs };

    useEffect(() => {
        if (isLoading) {
            // Load start (only on the false→true transition; subsequent
            // renders with isLoading=true are no-ops).
            if (startTimeRef.current != null) return;

            startTimeRef.current = Date.now();
            firedRef.current = false;
            setElapsedSeconds(0);
            setShowOverlay(false);

            overlayTimerRef.current = setTimeout(() => {
                setShowOverlay(true);
            }, showOverlayDelayMs);

            tickIntervalRef.current = setInterval(() => {
                if (startTimeRef.current != null) {
                    setElapsedSeconds(
                        Math.floor((Date.now() - startTimeRef.current) / 1000),
                    );
                }
            }, 1000);

            return;
        }

        // Load end — fires once on the true→false transition. Subsequent
        // renders with isLoading=false short-circuit because
        // startTimeRef has been cleared.
        if (startTimeRef.current == null) return;

        const elapsed = Date.now() - startTimeRef.current;

        if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);
        if (tickIntervalRef.current) clearInterval(tickIntervalRef.current);
        overlayTimerRef.current = null;
        tickIntervalRef.current = null;
        startTimeRef.current = null;
        setShowOverlay(false);
        setElapsedSeconds(0);

        // Always drain the request-id set on load end — even when we
        // do not POST telemetry. Otherwise IDs captured during a
        // sub-threshold load would leak into the next load's payload
        // and corrupt the correlation back to backend timings.
        const requestIds = snapshotAndClearRequestIds();
        const props = propsRef.current;
        if (elapsed > props.telemetryThresholdMs && !firedRef.current) {
            firedRef.current = true;
            postPageLoadTelemetry({
                page: props.page,
                view: props.view,
                elapsed_ms: Math.round(elapsed),
                page_index: props.pageIndex,
                page_size: props.pageSize,
                cache_hit: props.cacheHit,
                request_ids: requestIds,
            });
        }
    }, [isLoading, showOverlayDelayMs]);

    // Unmount cleanup — must NOT clear startTimeRef in the deps-driven
    // effect above because that would also fire on every prop change.
    useEffect(() => {
        return () => {
            if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);
            if (tickIntervalRef.current) clearInterval(tickIntervalRef.current);
        };
    }, []);

    return { showOverlay, elapsedSeconds };
}
